/**
 * The canonical order lifecycle.
 *
 * NOTE: the app currently uses four different vocabularies for these same
 * states (see docs/fulfilment-ux-review.md §1). This module is the intended
 * single source of truth — the labels below match the ViewOrder stepper, which
 * is the most complete model. When the vocabulary unification lands, this is
 * the one file that changes.
 */
import {
  CheckCircle,
  ClipboardList,
  Package,
  Truck,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import type { Order } from '@/types/domain';

export interface JourneyStep {
  key: string;
  label: string;
  icon: LucideIcon;
}

export const ORDER_JOURNEY_STEPS: JourneyStep[] = [
  { key: 'confirmed', label: 'Order Confirmed', icon: ClipboardList },
  { key: 'processing', label: 'Sent to Warehouse', icon: Warehouse },
  { key: 'packed', label: 'Packed', icon: Package },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle },
];

type ProgressInput = Pick<Order, 'status' | 'packages_json'> | null | undefined;

/**
 * Index of the current step in ORDER_JOURNEY_STEPS.
 *
 * Mirrors ViewOrder's getOrderProgress exactly so the two surfaces cannot
 * disagree about where an order is. ViewOrder should import this rather than
 * keeping its local copy.
 */
export function getOrderProgress(order: ProgressInput): number {
  const status = order?.status?.toLowerCase();

  if (status === 'delivered') return 4;
  if (status === 'shipped') return 3;

  const packages = order?.packages_json;
  if (Array.isArray(packages) && packages.length > 0) {
    if (status === 'processing' || status === 'confirmed') return 2;
  }

  if (status === 'processing') return 1;
  if (status === 'confirmed' || status === 'pending' || status === 'open') return 0;
  return 0;
}

/**
 * Status badge classes, following the convention recorded in
 * .interface-design/system.md: amber = draft, emerald = confirmed,
 * blue = shipped, purple = invoiced.
 */
export function getOrderStatusClasses(status: string | null | undefined): string {
  switch (status?.toLowerCase()) {
    case 'confirmed':
    case 'open':
      return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    case 'shipped':
      return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    case 'delivered':
      return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    case 'invoiced':
      return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
    case 'draft':
    case 'pending':
      return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
    case 'cancelled':
    case 'void':
      return 'bg-destructive/10 text-destructive border-destructive/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}
